import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Pencil, Wrench } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarEdit } from "@/components/Avatar";
import { OFF_KINDS, backLabel, offDays, offLabel, repairOverdue } from "@/lib/constants";
import { todayIso } from "@/lib/dates";

export const HandoffNote = ({ note, testid }) => (
  <div data-testid={testid} className="rounded-lg border border-sky-500/40 bg-sky-500/10 px-3 py-2 text-xs">
    <div className="font-bold uppercase tracking-wide text-sky-300">Repair note for next rider</div>
    <p className="mt-0.5 text-foreground">{note.text}</p>
    <div className="mt-1 text-[10px] text-muted-foreground">{note.by}{note.acknowledged_by ? ` · read by ${note.acknowledged_by}` : ""}</div>
  </div>
);

const ReturnDialog = ({ bike, onClose, onSaved }) => {
  const [note, setNote] = useState("");
  const save = async () => {
    try {
      const { data } = await api.post(`/bikes/${bike.id}/status`, { status: "available", handoff_note: note });
      toast.success(`${bike.name} returned to service`, { description: note.trim() ? "The next rider will see your note." : undefined });
      onSaved(data);
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="return-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Return {bike.name} to service</DialogTitle></DialogHeader>
        <div className="space-y-1.5"><Label>Note for the next rider (optional)</Label>
          <Textarea rows={3} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. New front pads fitted - allow 50 miles to bed in" data-testid="return-note-input" /></div>
        <p className="text-xs text-muted-foreground">The note is shown at the next pre-ride check, then saved with that ride.</p>
        <button onClick={save} data-testid="return-save-btn" className="w-full rounded-full bg-primary py-3 text-sm font-bold text-primary-foreground">Return to service</button>
      </DialogContent>
    </Dialog>
  );
};

const OffInfo = ({ b, canManage, onChange }) => {
  const late = b.expected_back && b.expected_back < todayIso();
  const setBack = async (v) => {
    try { const { data } = await api.patch(`/bikes/${b.id}/expected-back`, { expected_back: v || null }); toast.success("Return date updated"); onChange(data); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-2 text-xs" data-testid={`bike-offinfo-${b.id}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-muted-foreground">Off road {offDays(b)} day{offDays(b) === 1 ? "" : "s"}</span>
        {repairOverdue(b) && <span data-testid={`bike-over7-${b.id}`} className="rounded bg-amber-500/25 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-200">Over 7 days</span>}
        {late && <span data-testid={`bike-late-${b.id}`} className="rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">Return overdue</span>}
      </div>
      {canManage ? (
        <label className="flex items-center gap-2"><span className="text-muted-foreground">Expected back</span>
          <input type="date" defaultValue={b.expected_back || ""} onBlur={(e) => e.target.value !== (b.expected_back || "") && setBack(e.target.value)} data-testid={`bike-expected-back-${b.id}`} className="h-8 rounded-md border bg-background px-2 text-xs" /></label>
      ) : <div data-testid={`bike-back-label-${b.id}`} className="font-semibold text-sky-300">{backLabel(b)}</div>}
    </div>
  );
};

export const BikeStatus = ({ bike }) => {
  if (bike.status === "off_road") return <span data-testid={`bike-status-${bike.id}`} className="rounded-full border border-red-500/40 bg-red-500/15 px-2.5 py-0.5 text-xs font-bold text-red-300">{offLabel(bike)}</span>;
  if (bike.current_run_id) return <span data-testid={`bike-status-${bike.id}`} className="rounded-full border border-sky-500/40 bg-sky-500/15 px-2.5 py-0.5 text-xs font-bold text-sky-300">Out on run</span>;
  return <span data-testid={`bike-status-${bike.id}`} className="rounded-full border border-emerald-500/40 bg-emerald-500/15 px-2.5 py-0.5 text-xs font-bold text-emerald-300">Available</span>;
};

const EditBike = ({ bike, areas, onClose, onSaved }) => {
  const [f, setF] = useState({ name: bike?.name || "", type: bike?.type || "bike", area_id: bike?.area_id || null, registration: bike?.registration || "", base: bike?.base || "", make_model: bike?.make_model || "", notes: bike?.notes || "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = bike ? await api.patch(`/bikes/${bike.id}`, f) : await api.post("/bikes", f);
      toast.success("Bike saved");
      onSaved(data);
      onClose();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="bike-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{bike ? "Edit bike" : "Add bike"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-1.5"><Label>Bike name</Label><Input required value={f.name} onChange={set("name")} data-testid="bike-name-input" /></div>
          <div className="space-y-1.5"><Label>Type</Label>
            <div className="flex gap-2">{["bike", "car"].map((t) => <button type="button" key={t} onClick={() => setF({ ...f, type: t })} data-testid={`bike-type-${t}`} className={`rounded-full border px-4 py-1.5 text-sm capitalize ${f.type === t ? "border-primary bg-primary/15 text-primary" : "text-muted-foreground"}`}>{t}</button>)}</div>
          </div>
          <div className="space-y-1.5"><Label>Registration</Label><Input value={f.registration} onChange={set("registration")} data-testid="bike-reg-input" /></div>
          <div className="space-y-1.5"><Label>Make / model</Label><Input value={f.make_model} onChange={set("make_model")} data-testid="bike-model-input" /></div>
          <div className="space-y-1.5"><Label>Base location</Label><Input value={f.base || ""} onChange={set("base")} data-testid="bike-base-input" /></div>
          <div className="space-y-1.5"><Label>Area</Label>
            <div className="flex flex-wrap gap-2">{areas.map((a) => <button type="button" key={a.id} onClick={() => setF({ ...f, area_id: a.id })} data-testid={`bike-area-${a.id}`} style={{ borderColor: a.color, background: f.area_id === a.id ? a.color : "transparent" }} className={`rounded-full border-2 px-3 py-1 text-xs font-semibold ${f.area_id === a.id ? "text-white" : "text-muted-foreground"}`}>{a.name}</button>)}</div>
          </div>
          <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={f.notes} onChange={set("notes")} data-testid="bike-notes-input" /></div>
          <div className="flex justify-end"><button type="submit" data-testid="bike-save-btn" className="rounded-full bg-primary px-6 py-2.5 text-sm font-bold text-primary-foreground">Save</button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const OffRoad = ({ bike, onClose, onSaved }) => {
  const [reason, setReason] = useState("");
  const [kind, setKind] = useState("repair");
  const [back, setBack] = useState("");
  const save = async () => {
    try {
      const { data } = await api.post(`/bikes/${bike.id}/status`, { status: "off_road", reason, kind, expected_back: back || null });
      toast.success(`${bike.name} marked ${OFF_KINDS[kind].toLowerCase()}`);
      onSaved(data);
      onClose();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="offroad-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Take {bike.name} off road</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Riders cannot select, book or start a pre-ride check on this vehicle until it is returned to service.</p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(OFF_KINDS).map(([k, l]) => <button key={k} type="button" onClick={() => setKind(k)} data-testid={`offroad-kind-${k}`} className={`rounded-full border-2 px-4 py-1.5 text-sm font-semibold ${kind === k ? "border-red-500 bg-red-500/20 text-red-200" : "text-muted-foreground"}`}>{l}</button>)}
        </div>
        <Textarea rows={3} placeholder="Reason (e.g. rear brake pads worn)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="offroad-reason-input" />
        <div className="space-y-1.5"><Label>Expected back in service (optional)</Label>
          <Input type="date" min={todayIso()} value={back} onChange={(e) => setBack(e.target.value)} data-testid="offroad-expected-back-input" /></div>
        <div className="flex justify-end"><button onClick={save} data-testid="offroad-confirm-btn" className="rounded-full bg-destructive px-6 py-2.5 text-sm font-bold text-white">Mark off road</button></div>
      </DialogContent>
    </Dialog>
  );
};

export const BikeCards = ({ bikes, canManage, onChange, onAdd }) => {
  const [areas, setAreas] = useState([]);
  useEffect(() => { api.get("/rota/setup").then((r) => setAreas(r.data.areas)).catch(() => {}); }, []);
  const areaOf = (b) => areas.find((a) => a.id === b.area_id);
  const [edit, setEdit] = useState(null);
  const [off, setOff] = useState(null);
  const [ret, setRet] = useState(null);
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {bikes.map((b, i) => (
          <article key={b.id} data-testid={`bike-card-${b.id}`} className={`rise-in overflow-hidden rounded-2xl border bg-card ${b.status === "off_road" ? "border-red-500/50" : ""}`} style={{ animationDelay: `${i * 60}ms` }}>
            <div className={`h-2 ${b.status === "off_road" ? "bg-destructive" : "chevron-stripe"}`} />
            <div className="space-y-3 p-5">
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 items-start gap-3">
                  {canManage ? <AvatarEdit photo={b.photo} name={b.name} size="md" square endpoint={`/avatars/bike/${b.id}`} onSaved={(p) => onChange({ ...b, photo: p })} testid={`bike-photo-${b.id}`} /> : <Avatar photo={b.photo} name={b.name} size="md" square testid={`bike-photo-${b.id}`} />}
                <div>
                  <Link to={`/fleet/bikes/${b.id}`} data-testid={`bike-link-${b.id}`} className="text-lg font-extrabold hover:text-primary">{b.name}</Link>
                  <div className="font-mono text-xs text-muted-foreground">{b.registration || "Reg not set"}{b.make_model ? ` · ${b.make_model}` : ""}</div>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${b.type === "car" ? "bg-cyan-500/20 text-cyan-300" : "bg-yellow-400/20 text-yellow-300"}`}>{b.type === "car" ? "Car" : "Bike"}</span>
                    {areaOf(b) && <span className="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase text-white" style={{ background: areaOf(b).color }}>{areaOf(b).name}</span>}
                    {b.base && <span className="text-[11px] text-muted-foreground">{b.base}</span>}
                  </div>
                </div>
                </div>
                <BikeStatus bike={b} />
              </div>
              {b.status === "off_road" && <p data-testid={`bike-offroad-reason-${b.id}`} className="rounded-lg bg-red-500/10 px-3 py-2 text-xs text-red-300">{b.off_road_reason}</p>}
              {b.status === "off_road" && <OffInfo b={b} canManage={canManage} onChange={onChange} />}
              {b.status !== "off_road" && b.handoff_note && <HandoffNote note={b.handoff_note} testid={`bike-handoff-${b.id}`} />}
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span>Miles: <span className="font-mono text-foreground">{b.last_mileage ?? "—"}</span></span>
                <span className={b.open_faults ? "font-semibold text-amber-400" : ""}><AlertTriangle className="mr-1 inline h-3 w-3" />{b.open_faults} open fault{b.open_faults === 1 ? "" : "s"}</span>
              </div>
              {canManage && (
                <div className="flex flex-wrap gap-2 border-t pt-3">
                  <button onClick={() => setEdit(b)} data-testid={`bike-edit-${b.id}`} className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold hover:bg-secondary"><Pencil className="h-3 w-3" /> Edit</button>
                  {b.status === "off_road" ? (
                    <button onClick={() => setRet(b)} data-testid={`bike-return-${b.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 px-3 py-1.5 text-xs font-semibold text-emerald-300 hover:bg-emerald-500/10"><CheckCircle2 className="h-3 w-3" /> Return to service</button>
                  ) : (
                    <button onClick={() => setOff(b)} data-testid={`bike-offroad-${b.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-red-500/40 px-3 py-1.5 text-xs font-semibold text-red-300 hover:bg-red-500/10"><Wrench className="h-3 w-3" /> Off road</button>
                  )}
                </div>
              )}
            </div>
          </article>
        ))}
      </div>
      {canManage && <button onClick={() => setEdit("new")} data-testid="add-bike-btn" className="rounded-full border px-4 py-2 text-sm font-semibold hover:bg-secondary">+ Add bike</button>}
      {edit && <EditBike bike={edit === "new" ? null : edit} areas={areas} onClose={() => setEdit(null)} onSaved={edit === "new" ? onAdd : onChange} />}
      {off && <OffRoad bike={off} onClose={() => setOff(null)} onSaved={onChange} />}
      {ret && <ReturnDialog bike={ret} onClose={() => setRet(null)} onSaved={onChange} />}
    </div>
  );
};
